import React, { useState } from "react";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import "./SignUp.css"; // Import the CSS file
import { FaArrowLeft } from "react-icons/fa";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import TermsModal from "../components/TermsModal";
import Analytics from "../components/GoogleAnalytics";

const SignUp = ({ onSignUp }) => {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const navigate = useNavigate();

  const handleSignUp = async () => {
    const newErrors = {};

    // Basic validation (including firstname and lastname)
    if (!firstname) newErrors.firstname = "First name is required.";
    if (!lastname) newErrors.lastname = "Last name is required.";
    if (!email) newErrors.email = "Email is required.";
    if (!phone) newErrors.phone = "Phone number is required.";
    if (!password) newErrors.password = "Password is required.";
    if (!age) newErrors.age = "Age is required.";
    if (!gender) newErrors.gender = "Gender is required.";
    if (!agreedToTerms) newErrors.terms = "You must agree to the Terms and Conditions.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("firstname", firstname);
      formData.append("lastname", lastname);
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("password", password);
      formData.append("age", age);
      formData.append("gender", gender);
      const response = await fetch(
        `${API_BASE_URL}/register`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (data.statusCode === 1) {
          // Call login-site API after successful signup
          try {
            const loginResponse = await fetch(
              `${API_BASE_URL}/login-site`,
              {
                method: "POST",
                body: JSON.stringify({
                  email,
                  phone,
                  password,
                }),
              }
            );
            const loginData = await loginResponse.json();
            if (loginResponse.ok && loginData.statusCode === 1) {
              sessionStorage.setItem("user", JSON.stringify(loginData));
              navigate(`/`);
            } else {
              setErrors({
                api: "Signup successful, but automatic login failed. Please login manually.",
              });
              navigate(`/login`);
            }
          } catch (loginError) {
            setErrors({
              api: "Signup successful, but automatic login failed. Please login manually.",
            });
            navigate(`/login`);
          }
        } else {
          const apiErrors = data.statusMessage.reduce((acc, error) => {
            if (typeof error === "string") {
              return { ...acc, api: error };
            }
            return { ...acc, api: "An unknown error occurred." };
          }, {});
          setErrors(apiErrors);
        }
      } else {
        // Handle API error response
        const apiErrors = data.statusMessage.reduce((acc, error) => {
          if (typeof error === "string") {
            return { ...acc, api: error };
          }
          return { ...acc, api: "An unknown error occurred." };
        }, {});
        setErrors(apiErrors);
      }
    } catch (error) {
      navigate(`/`);
    } finally {
      setLoading(false); // Reset loading state
    }
  };
  return (
    <div className="container d-flex justify-content-center align-items-center vh-100">
      <Analytics />
      <div
        className="card p-4 signup-card"
        style={{ maxWidth: "500px", width: "100%" }}
      >
        <div className="d-flex align-items-center justify-content-between mb-4">
          <button
            className="btn btn-link text-decoration-none d-flex align-items-center"
            onClick={() => navigate("/")}
          >
            <FaArrowLeft className="me-2" />
            Home
          </button>
          <h2 style={{ width: "60%" }} className="text-left mb-1">
            Sign Up
          </h2>
        </div>
        <div className="d-flex align-content-center gap-2">
          <input
            type="text"
            className="form-control mb-2"
            placeholder="First Name"
            maxLength={50}
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
            required
          />
          {errors.firstname && <div className="text-danger">*</div>}

          <input
            type="text"
            className="form-control mb-2"
            placeholder="Last Name"
            maxLength={50}
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            required
          />
          {errors.lastname && <div className="text-danger">*</div>}
        </div>
        <div className="d-flex gap-2">
          <input
            type="number"
            className="form-control mb-2"
            placeholder="Enter your age"
            value={age}
            onChange={(e) => {
              const value = e.target.value;
              if (value <= 120) {
                setAge(e.target.value);
              }
            }}
            min={1}
            max={120}
            required
          />
          {errors.age && <div className="text-danger">*</div>}

          <select
            className="form-control mb-2"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
          >
            <option disabled value="">
              Select Gender
            </option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          {errors.gender && <div className="text-danger">*</div>}
        </div>
        <input
          type="email"
          className="form-control mb-2"
          placeholder="Enter your email"
          maxLength={64}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {errors.email && <div className="text-danger">{errors.email}</div>}

        <input
          type="number"
          className="form-control mb-2"
          placeholder="Enter your phone number"
          value={phone}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 14) {
              setPhone(value);
            }
          }}
        />
        {errors.phone && <div className="text-danger">{errors.phone}</div>}

        <div className="mb-2 position-relative">
          <input
            type={showPassword ? "text" : "password"}
            className="form-control"
            placeholder="Enter your password"
            maxLength={14}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{ paddingRight: "40px" }}
          />
          <span
            style={{
              position: "absolute",
              right: "15px",
              top: "38%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: "#6c757d",
              zIndex: 2,
            }}
            onClick={() => setShowPassword((v) => !v)}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
        {errors.password && (
          <div className="text-danger">{errors.password}</div>
        )}
        {errors.api && <div className="text-danger">{errors.api}</div>}

        {/* Terms and Conditions Section */}
        <div className="mb-2">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="termsCheckbox"
              checked={agreedToTerms}
              onChange={e => setAgreedToTerms(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="termsCheckbox">
              I Agree to the <span style={{color: '#007bff', cursor: 'pointer'}} onClick={() => setShowTerms(true)}>Terms and Conditions</span>
            </label>
          </div>
          {errors.terms && <div className="text-danger">{errors.terms}</div>}
        </div>
        <TermsModal show={showTerms} onClose={() => setShowTerms(false)} />

        <button
          className="btn btn-primary w-100 mt-2"
          onClick={handleSignUp}
          disabled={loading || !agreedToTerms}
        >
          {loading ? "Signing Up..." : "Sign Up"}
        </button>

        <div className="text-center mt-3">
          Already have an account?{" "}
          <span
            className="text-primary text-decoration-underline"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/login/home")}
          >
            Login
          </span>
        </div>
      </div>
    </div>
  );
};

export default SignUp;